import React from "react";
import { Timestamp } from "firebase/firestore";
import { Odd } from "../../../../models/Odd_response_model.tsx";
import { TeaserSelectorComponent } from "./TeaserSelectorComponent.tsx";
import { ParlaySelection } from "../../../../models/MyBetModel.tsx";
import { useUser } from "../../../../context/UserContext.tsx";
import { useModal } from "../../../../context/ModalContext.tsx";
import TeaserBets from '../../modals/teaser/TeaserBets.tsx';
import { useBetContext } from "../../../../context/ParlayBetContext.tsx";
import { isSameSelectionSide, isSoccerLeague, preferPrimarySportsbookOdds, shouldUseFullTeamName } from "../../../../helpers/functions.tsx";
import ImageWithPlaceholder from "../../../ImageWithPlaceholder.tsx";
import { DEFAULT_PROPS_LOGO, logoForSelectionLine } from "../../modals/BetLogo.tsx";

interface GameTeamComponentProps {
    gameId: string,
    sport: string,
    team: string,
    odds: Odd[],
    league: string,
    gameTime: Date;
    setShowLimitAlert: React.Dispatch<React.SetStateAction<boolean>>,
    isHomeTeam: boolean,
    fixture_id: string,
    gameLogo: string,
    teaserPoints: number,
    teaserType: string,
    opposingTeam: string,
    setErrorMessage: React.Dispatch<React.SetStateAction<string>>;
}

export function TeaserGameTeam(props: GameTeamComponentProps) {

    const { gameId, sport, team, odds, league, fixture_id, gameLogo, setShowLimitAlert, teaserPoints, teaserType, opposingTeam, setErrorMessage } = props;
    const { bets, updateBetTeaser, deleteBet, setTeaserPoints, setTeaserType } = useBetContext();
    const teamBets = (bets ?? []).filter(existingBet => existingBet.gameId === gameId && existingBet.teamId === team);
    const gameTime = Timestamp.fromDate(props.gameTime);
    const spreadBet = teamBets?.find(b => b.marketName?.trim() === 'Point Spread' || b.marketName?.trim() === 'Run Line' || b.marketName?.trim() === 'Puck Line');
    const totalBet = teamBets?.find(b => b.marketName?.trim() === 'Total Points' || b.marketName?.trim() === 'Total Runs' || b.marketName?.trim() === 'Total Goals');
    const safeOdds = odds ?? [];
    const spread = safeOdds.filter(odd =>
        ['Point Spread', 'Run Line', 'Puck Line'].includes(odd.market?.trim())
    );
    const total = safeOdds.filter(odd =>
        ['Total Points', 'Total Runs', 'Total Goals'].includes(odd.market?.trim())
    );
    const { user } = useUser();
    if (!user?.userName) {
        throw new Error("User not authenticated");
    }

    const { showModal } = useModal();

    const onShowBetsModal = () => {
        showModal(
            <TeaserBets
                setShowLimitAlert={setShowLimitAlert}
                setErrorMessage={setErrorMessage}
            />
        );
    };

    const _build = (selected: Odd, odds: Odd[]): ParlaySelection => {

        const logoToShow = logoForSelectionLine(selected.selection_line, gameLogo || "");

        const newBet = {
            id: selected.id,
            gameId: gameId,
            league: league,
            sport: sport,
            marketName: selected.market,
            gameDate: gameTime,
            teamId: team,
            betName: selected.name,
            fixture_id: fixture_id,
            betStatus: 'pending',
            odd: selected,
            odds,
            opposingTeam: opposingTeam,
            logo: logoToShow
        } as ParlaySelection;

        return newBet;
    };

    const onRequestBetsModal = (update?: Partial<ParlaySelection>, selected: boolean = true) => {
        if (!update?.odd) return;

        if (selected && user?.status !== "Active") {
            setErrorMessage("Your account is in read-only mode.");
            return;
        }

        const odd = update.odd;
        const filteredOdds = preferPrimarySportsbookOdds(safeOdds.filter(o =>
            o.market_id === odd.market_id &&
            isSameSelectionSide(odd, o)
        ));
        const preferredOdd = filteredOdds.find(item => item.id === odd.id) ?? filteredOdds[0] ?? odd;

        if (selected) {
            setTeaserType(teaserType)
            setTeaserPoints(teaserPoints)
            updateBetTeaser(_build(preferredOdd, filteredOdds));
        } else {
            deleteBet(update);
        }
    };

    const totalBets = bets.length;

    function getShortName(team: string) {
        const directions: Record<string, string> = {
            "East": "E",
            "West": "W",
            "North": "N",
            "South": "S"
        };

        const specialAbbreviations: Record<string, string> = {
            "Georgia": "GA",
            "Middle": "Mid",
            "Louisiana": "LA"
        };

        const words = team.split(" ");

        if (directions[words[0]]) {
            words[0] = directions[words[0]];
            return words.length > 1 ? `${words[0]} ${words[1]}` : words[0];
        }

        if (specialAbbreviations[words[0]]) {
            words[0] = specialAbbreviations[words[0]];
            return words.length > 1 ? `${words[0]} ${words[1]}` : words[0];
        }

        if (words[0] === "New" && words.length > 1) {
            const secondWord = words[1];
            const lastWord = words[words.length - 1] === "State" ? "St" : null;
            return lastWord ? `N ${secondWord} ${lastWord}` : `N ${secondWord}`;
        }

        if (words[words.length - 1] === "State") {
            return words.length > 1 ? `${words[0]} St` : "St";
        }

        return words[words.length - 1];
    }

    return (
        <div className="line-container">
            {bets.length !== 0 && (
                <div className='containerButtonParlay'>
                    <button className='btn-custom' onClick={onShowBetsModal}>{totalBets} Pick(s)</button>
                </div>
            )}
            <div className="team-name-container">
                <ImageWithPlaceholder
                    src={gameLogo || ""}
                    className="sport-logos"
                    alt=""
                    placeholderClassName="placeholder-logos-team"
                    fallbackSrc={DEFAULT_PROPS_LOGO}
                />
                <span className="teko team-name name-small" title={team}>{team}</span>
                <span className="teko team-name name-big" title={team}>{shouldUseFullTeamName(league) || isSoccerLeague(league) ? team : getShortName(team)}</span>
            </div>
            <div className="spread-container">
                <TeaserSelectorComponent
                    availableOdds={spread}
                    initialOdd={spreadBet?.odd}
                    onRequestBetsModal={onRequestBetsModal}
                    market='spread'
                    group='moneyline_spread'
                    oddPrefix="S"
                    teaserType={teaserType}
                    setErrorMessage={setErrorMessage}
                    user={user}
                ></TeaserSelectorComponent>
            </div>

            <div className="moneyline-container d-none d-lg-block"></div>
            <div className="total-container">
                <TeaserSelectorComponent
                    availableOdds={total}
                    initialOdd={totalBet?.odd}
                    onRequestBetsModal={onRequestBetsModal}
                    market='total'
                    group='total'
                    oddPrefix="total"
                    teaserType={teaserType}
                    setErrorMessage={setErrorMessage}
                    user={user}

                ></TeaserSelectorComponent>
            </div>
            <div className="over-under-container d-none d-lg-block d-flex d-lg-flex align-items-center"></div>
        </div>
    )
}
